/* eslint-disable react-native/no-inline-styles */
/**
 * HowItWorks — Persistent self-service help screen
 *
 * Third canonical surface (Sprint 36 E1-S2-T1 guidance architecture):
 * - S01 TwoVoicesIntroBanner: RETIRED
 * - S13 WhatsNewIntroBanner: re-engagement cluster — entry point to What\'s New system
 * - HowItWorks: persistent self-service help, open to anonymous users
 *
 * This is a RECOVERY PATH, not a replacement for onboarding. It surfaces
 * the information a confused or returning user needs without disrupting flow.
 * Anonymous access is enforced in auth-route-access.ts via isHelpRoute.
 *
 * Access:
 * - Nav bar or Help menu link stub (placement TBD in S37 design pass) (DD-261 web header menu)
 * - Deep link: /how-it-works
 * - Settings > Help & Support > How It Works
 *
 * Created: Sprint 36 E1-S2-T2
 * Design pass: deferred to S37
 */
import { ScrollView, View, Pressable } from 'react-native';
import { Stack, useRouter } from 'expo-router';
import { TierHeadline, TierBody } from '@dramadetector/ui';
import { useTranslation } from '@dramadetector/shared';
import { Ionicons } from '@expo/vector-icons';

// TODO(S37): Replace with styled Section component from shared UI library
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View className="mb-8">
      <TierHeadline size="lg" weight="semibold" className="text-white mb-3">
        {title}
      </TierHeadline>
      {children}
    </View>
  );
}

export default function HowItWorksScreen() {
  const router = useRouter();
  // TODO(S37): Add 'how_it_works' i18n namespace
  // Using inline strings for now — copy will be extracted in S37 design pass
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t: _t } = useTranslation('common');

  return (
    <>
      <Stack.Screen
        options={{
          title: 'How It Works',
          headerShown: true,
          headerBackTitle: 'Back',
          headerStyle: { backgroundColor: '#0a0012' },
          headerTintColor: '#ffffff',
        }}
      />
      <ScrollView
        className="flex-1 bg-[#0a0012]"
        contentContainerStyle={{ padding: 24, paddingBottom: 48 }}
        testID="how-it-works-screen"
      >
        {/* Page header */}
        <View className="mb-8">
          <TierHeadline size="xl" weight="bold" className="text-white mb-2">
            How DramaDetector Works
          </TierHeadline>
          <TierBody size="sm" className="text-gray-400">
            Everything you need to know about understanding your analyses.
          </TierBody>
        </View>

        {/* Section 1: What drama detection is */}
        <Section title="What is drama detection?">
          <TierBody size="sm" className="text-gray-300 leading-relaxed">
            DramaDetector uses AI to analyse messages for conflict patterns,
            emotional intensity, and communication style. It surfaces patterns
            that are easy to miss in the moment — helping you respond more
            clearly and intentionally.
          </TierBody>
        </Section>

        {/* Section 2: How to submit content */}
        <Section title="How to analyse a message">
          <TierBody size="sm" className="text-gray-300 leading-relaxed mb-2">
            1. Paste or type the message you want to analyse into the input box
            on the Home tab.
          </TierBody>
          <TierBody size="sm" className="text-gray-300 leading-relaxed mb-2">
            2. Click <TierBody size="sm" weight="semibold" className="text-white">Analyse</TierBody>.
            Results appear in seconds.
          </TierBody>
          <TierBody size="sm" className="text-gray-300 leading-relaxed">
            3. Click any section of the result to expand the explanation and see
            the suggested rewrite.
          </TierBody>
        </Section>

        {/* Section 3: What the analysis output means */}
        <Section title="Understanding your results">
          <TierBody size="sm" className="text-gray-300 leading-relaxed mb-2">
            <TierBody size="sm" weight="semibold" className="text-white">Drama Score:</TierBody>
            {' '}A 0–100 measure of the message&apos;s conflict potential. Higher
            scores indicate more emotionally charged language.
          </TierBody>
          <TierBody size="sm" className="text-gray-300 leading-relaxed mb-2">
            <TierBody size="sm" weight="semibold" className="text-white">Patterns:</TierBody>
            {' '}Named communication patterns found in the message (e.g.
            blame-shifting, catastrophising, stonewalling).
          </TierBody>
          <TierBody size="sm" className="text-gray-300 leading-relaxed">
            <TierBody size="sm" weight="semibold" className="text-white">Rewrite suggestion:</TierBody>
            {' '}A calmer, clearer version of the original message you can adapt and use.
          </TierBody>
        </Section>

        {/* Section 4: Free vs premium */}
        <Section title="Free vs Drama+">
          <TierBody size="sm" className="text-gray-300 leading-relaxed mb-2">
            <TierBody size="sm" weight="semibold" className="text-white">Free tier:</TierBody>
            {' '}5 analyses per day. Single-voice analysis (Drama Expert).
            Full result history.
          </TierBody>
          <TierBody size="sm" className="text-gray-300 leading-relaxed">
            <TierBody size="sm" weight="semibold" className="text-white">Drama+:</TierBody>
            {' '}Unlimited analyses. Dual-voice analysis (Narrator + Drama Expert
            perspectives). Messaging feature. Priority support.
          </TierBody>
        </Section>

        {/* Section 5: How to get help */}
        <Section title="Getting help">
          <TierBody size="sm" className="text-gray-300 leading-relaxed mb-4">
            If something isn&apos;t working as expected, or you have a question not
            answered here, reach out via Settings → Contact Support.
          </TierBody>
          {/* TODO(S37): Wire to actual support navigation */}
          <Pressable
            className="flex-row items-center gap-2 py-3 px-4 bg-white/10 rounded-xl"
            onPress={() => router.push('/(tabs)/settings')}
            accessibilityRole="button"
            accessibilityLabel="Go to Settings"
          >
            <Ionicons name="settings-outline" size={20} color="#9CA3AF" />
            <TierBody size="sm" className="text-gray-300">
              Open Settings
            </TierBody>
          </Pressable>
        </Section>

        {/* Nav stub note */}
        {/* TODO(S37): Add persistent help link in header/nav bar (DD-261 web header menu). */}
        {/* This route is accessible via /how-it-works deep link and Settings > Help. */}
      </ScrollView>
    </>
  );
}
